import { useMemo, useState } from 'react';
import { InfoCard } from './common';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Search, ExternalLink, ChevronDown, ChevronUp, MapPin } from 'lucide-react';
import { SCHEMES, SCHEME_CATEGORIES, INDIAN_STATES } from '@/data/schemes';

const CAT_COLOR = {
  'Income Support': 'bg-emerald-100 text-emerald-800',
  'Crop Insurance': 'bg-sky-100 text-sky-800',
  'Irrigation': 'bg-cyan-100 text-cyan-800',
  'Credit & Loans': 'bg-purple-100 text-purple-800',
  'Price Support': 'bg-orange-100 text-orange-800',
  'Seed & Input Subsidy': 'bg-lime-100 text-lime-800',
  'Organic Farming': 'bg-teal-100 text-teal-800',
  'Soil & Inputs': 'bg-amber-100 text-amber-800',
  'Infrastructure': 'bg-slate-100 text-slate-800',
  'Farmer Collective': 'bg-rose-100 text-rose-800',
};

const Schemes = () => {
  const [category, setCategory] = useState('All');
  const [state, setState] = useState('All India');
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return SCHEMES.filter((s) => {
      const catOk = category === 'All' || s.category === category;
      const stateOk = state === 'All India' || !s.states.length || s.states.includes(state);
      const searchOk = !q ||
        s.name.toLowerCase().includes(q) ||
        s.description.toLowerCase().includes(q) ||
        s.category.toLowerCase().includes(q) ||
        (s.acronyms || []).some(a => a.toLowerCase().includes(q));
      return catOk && stateOk && searchOk;
    });
  }, [category, state, search]);

  return (
    <InfoCard
      title="Government Schemes"
      subtitle="Central + state agricultural schemes with eligibility, benefits, and how to apply — works fully offline."
    >
      <div className="grid gap-3 md:grid-cols-[1fr_200px_200px] mb-4">
        <div>
          <Label className="text-xs text-icrisat-earth">Search</Label>
          <div className="relative">
            <Search className="h-4 w-4 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="schemes-search"
              className="pl-8"
              placeholder="Search schemes…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
        <div>
          <Label className="text-xs text-icrisat-earth">Category</Label>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger data-testid="schemes-category"><SelectValue /></SelectTrigger>
            <SelectContent>{SCHEME_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-icrisat-earth">State</Label>
          <Select value={state} onValueChange={setState}>
            <SelectTrigger data-testid="schemes-state"><SelectValue /></SelectTrigger>
            <SelectContent>{INDIAN_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex items-center justify-between mb-3 text-sm">
        <span className="text-icrisat-earth"><b>{filtered.length}</b> scheme{filtered.length === 1 ? '' : 's'} found</span>
        <span className="text-xs text-emerald-700">Works offline</span>
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-10 text-muted-foreground">
          <div className="text-4xl mb-2">🔍</div>
          <div className="text-sm">No schemes match your filter.</div>
        </div>
      )}

      <div className="space-y-3">
        {filtered.map((s) => <SchemeCard key={s.id} scheme={s} />)}
      </div>
    </InfoCard>
  );
};

const SchemeCard = ({ scheme }) => {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={`scheme-card-${scheme.id}`} className="rounded-xl border border-icrisat-green/20 bg-white overflow-hidden shadow-sm">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="w-full text-left p-4 flex items-start gap-3 hover:bg-icrisat-green-soft/40 transition-colors"
      >
        <span className="text-3xl leading-none">{scheme.emoji}</span>
        <div className="flex-1 min-w-0">
          <div className="font-display text-icrisat-green text-base">{scheme.name}</div>
          <Badge className={`${CAT_COLOR[scheme.category] || 'bg-slate-100 text-slate-800'} mt-1 text-[10px]`}>
            {scheme.category}
          </Badge>
          <div className="text-xs text-emerald-800 font-medium mt-2 line-clamp-2">
            {scheme.benefit}
          </div>
        </div>
        {open ? <ChevronUp className="h-5 w-5 text-icrisat-earth shrink-0" />
              : <ChevronDown className="h-5 w-5 text-icrisat-earth shrink-0" />}
      </button>

      {open && (
        <div className="border-t border-border/60 p-4 space-y-3 bg-icrisat-cream/50 animate-fade-up">
          <Detail label="About" icon="📋" value={scheme.description} />
          <Detail label="Eligibility" icon="✅" value={scheme.eligibility} />
          <Detail label="How to Apply" icon="📝" value={scheme.howToApply} />
          <Detail label="Deadline" icon="⏰" value={scheme.deadline} />
          {scheme.states.length > 0 && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground italic">
              <MapPin className="h-3 w-3" />
              State scheme: {scheme.states.join(', ')}
            </div>
          )}
          <Button
            asChild
            variant="outline"
            className="border-icrisat-green text-icrisat-green hover:bg-icrisat-green hover:text-white"
            data-testid={`scheme-link-${scheme.id}`}
          >
            <a href={scheme.url} target="_blank" rel="noreferrer">
              <ExternalLink className="h-4 w-4 mr-1" /> Official website
            </a>
          </Button>
        </div>
      )}
    </div>
  );
};

const Detail = ({ icon, label, value }) => (
  <div className="flex items-start gap-2">
    <span className="text-base leading-none pt-0.5">{icon}</span>
    <div className="min-w-0">
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">{label}</div>
      <div className="text-sm text-icrisat-ink/90 whitespace-pre-line leading-relaxed">{value}</div>
    </div>
  </div>
);

export default Schemes;
